import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useLingui } from '@lingui/react/macro';
import { useEffect, useMemo, useState } from 'react';
import { trpc } from '@/lib/trpc';
import { usePortalNewFormStore, TOTAL_PORTAL_NEW_STEPS } from '@/lib/portal-new-form-store';
import { PortalNewProvider } from './-components/portal-new-context';
import { PortalNewLayout } from './-components/portal-new-layout';
import { seedStoreFromPortal } from './-components/portal-setup-seed';

export const Route = createFileRoute('/ngos/portals/$portalId_/setup')({
	component: RouteComponent,
});

function RouteComponent() {
	const { t } = useLingui();
	const { portalId } = Route.useParams();
	const navigate = useNavigate();
	const { data: portal } = useQuery(trpc.ngo.getPortal.queryOptions({ portalId }));
	const portalTitle = portal && 'title' in portal ? portal.title : undefined;
	const setPortalId = usePortalNewFormStore((s) => s.setPortalId);
	const storedPortalId = usePortalNewFormStore((s) => s.portalId);
	const seeded = usePortalNewFormStore((s) => !!(s.portalId && s._archive[s.portalId]?.seeded));
	const hasHydrated = usePortalNewFormStore((s) => s._hasHydrated);

	useEffect(() => {
		if (!hasHydrated) return;
		if (storedPortalId !== portalId) setPortalId(portalId);
	}, [hasHydrated, portalId, storedPortalId, setPortalId]);

	// Once per portal, or a late query overwrites typed values; an empty title does not mean "not started".
	useEffect(() => {
		if (!hasHydrated) return;
		if (!portal || 'notFound' in portal) return;
		if (storedPortalId !== portalId) return;
		if (seeded) return;
		seedStoreFromPortal(portal);
		usePortalNewFormStore.getState().markSeeded(portalId);
	}, [hasHydrated, portal, portalId, storedPortalId, seeded]);

	const navigation = useMemo(
		() => ({
			toLanguages: () => navigate({ to: '/ngos/portals/$portalId/setup', params: { portalId } }),
			toDetails: () => navigate({ to: '/ngos/portals/$portalId/setup/details', params: { portalId } }),
			toFunding: () => navigate({ to: '/ngos/portals/$portalId/setup/funding', params: { portalId } }),
			toExtras: () => navigate({ to: '/ngos/portals/$portalId/setup/extras', params: { portalId } }),
			toSuccess: () => navigate({ to: '/ngos/portals/$portalId/setup/success', params: { portalId } }),
			toCancel: () => navigate({ to: '/ngos/portals' }),
			toPortalDetail: () => navigate({ to: '/ngos/portals/$portalId', params: { portalId } }),
		}),
		[navigate, portalId],
	);

	const isEditing = !!(portal && !('notFound' in portal) && portal.updatedAt);

	const [stepNavContainer, setStepNavContainer] = useState<HTMLDivElement | null>(null);

	return (
		<PortalNewProvider
			value={{
				portalId,
				totalSteps: TOTAL_PORTAL_NEW_STEPS,
				navigation,
				isEditing,
				breadcrumbs: [
					{ label: t`Worldcoo Plus`, onClick: () => navigate({ to: '/ngos/portals' }) },
					{ label: portalTitle ?? portalId, onClick: navigation.toCancel },
				],
				stepNavContainer,
				setStepNavContainer,
			}}
		>
			<PortalNewLayout />
		</PortalNewProvider>
	);
}
